import React, { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Diamond, ArrowLeft, Download, ShieldCheck } from "lucide-react";
import { toPng } from "html-to-image";

const FIELD_LABELS = [
  ["full_name", "Full name"],
  ["mobile", "Mobile"],
  ["email", "Email"],
  ["password", "Password"],
  ["t_pass", "Transaction password"],
  ["sponsor_user_id", "Sponsor / Upline ID"],
];

const COUNTRY_CODES = [
  { iso: "IN", flag: "🇮🇳", code: "+91" },
  { iso: "US", flag: "🇺🇸", code: "+1" },
  { iso: "GB", flag: "🇬🇧", code: "+44" },
  { iso: "AE", flag: "🇦🇪", code: "+971" },
  { iso: "SA", flag: "🇸🇦", code: "+966" },
  { iso: "QA", flag: "🇶🇦", code: "+974" },
  { iso: "KW", flag: "🇰🇼", code: "+965" },
  { iso: "OM", flag: "🇴🇲", code: "+968" },
  { iso: "NP", flag: "🇳🇵", code: "+977" },
  { iso: "BD", flag: "🇧🇩", code: "+880" },
  { iso: "LK", flag: "🇱🇰", code: "+94" },
  { iso: "PK", flag: "🇵🇰", code: "+92" },
  { iso: "SG", flag: "🇸🇬", code: "+65" },
  { iso: "MY", flag: "🇲🇾", code: "+60" },
  { iso: "ID", flag: "🇮🇩", code: "+62" },
  { iso: "PH", flag: "🇵🇭", code: "+63" },
  { iso: "AU", flag: "🇦🇺", code: "+61" },
  { iso: "CA", flag: "🇨🇦", code: "+1" },
  { iso: "ZA", flag: "🇿🇦", code: "+27" },
  { iso: "NG", flag: "🇳🇬", code: "+234" },
  { iso: "KE", flag: "🇰🇪", code: "+254" },
  { iso: "DE", flag: "🇩🇪", code: "+49" },
  { iso: "FR", flag: "🇫🇷", code: "+33" },
];

export default function Register() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    full_name: "", country_code: "+91", mobile: "", email: "", password: "", t_pass: "",
    sponsor_user_id: params.get("ref") || "",
  });
  const [step, setStep] = useState("form"); // "form" | "confirm"
  const [accepted, setAccepted] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const receiptRef = useRef(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Step 1 → review. Native form validation runs before this fires.
  const onReview = (e) => {
    e.preventDefault();
    setErr("");
    setStep("confirm");
  };

  const downloadReceipt = async () => {
    if (!receiptRef.current) return;
    try {
      const dataUrl = await toPng(receiptRef.current, {
        cacheBust: true,
        backgroundColor: "#0A0A0A",
        pixelRatio: 2,
      });
      const link = document.createElement("a");
      const safe = (form.email || "account").replace(/[^a-z0-9]/gi, "_");
      link.download = `LTGlobal-Registration-${safe}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.warn("Receipt download failed:", e?.message);
    }
  };

  // Step 2 → confirm & submit: register, capture screenshot, then continue.
  const onConfirmSubmit = async () => {
    setErr("");
    setLoading(true);
    try {
      const payload = { ...form };
      payload.mobile = `${form.country_code}${form.mobile}`;
      delete payload.country_code;
      await register(payload);
      // Capture & download the confirmation as an image on the user's device.
      await downloadReceipt();
      navigate("/app");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <Diamond size={20} className="text-[#D4AF37]" />
          <span className="font-display text-2xl">LT Global Club</span>
        </Link>

        {step === "form" && (
          <div className="lt-card p-8" data-testid="register-form-card">
            <div className="overline">Membership</div>
            <h2 className="font-display text-3xl mt-2 mb-6">Open your account.</h2>
            <form onSubmit={onReview} className="space-y-4">
              <div>
                <label className="overline block mb-2">Full name</label>
                <input className="lt-input" data-testid="reg-name-input" value={form.full_name} onChange={update("full_name")} required />
              </div>
              <div>
                <label className="overline block mb-2">Mobile number</label>
                <div className="flex gap-2">
                  <select
                    className="lt-input shrink-0 px-2"
                    style={{ width: "118px", flex: "0 0 118px" }}
                    data-testid="reg-country-code-select"
                    value={form.country_code}
                    onChange={update("country_code")}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.iso} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input className="lt-input min-w-0" style={{ flex: "1 1 auto" }} data-testid="reg-mobile-input" inputMode="numeric" value={form.mobile} onChange={update("mobile")} required placeholder="Enter mobile number" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Email</label>
                <input className="lt-input" type="email" data-testid="reg-email-input" value={form.email} onChange={update("email")} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Password</label>
                  <input className="lt-input" type="password" data-testid="reg-password-input" value={form.password} onChange={update("password")} required minLength={6} />
                </div>
                <div>
                  <label className="overline block mb-2">Transaction password</label>
                  <input className="lt-input" type="password" data-testid="reg-tpass-input" value={form.t_pass} onChange={update("t_pass")} required minLength={4} />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Sponsor / Upline ID <span className="text-[#D4AF37] normal-case">(required)</span></label>
                <input className="lt-input" data-testid="reg-sponsor-input" value={form.sponsor_user_id} onChange={update("sponsor_user_id")} placeholder="e.g. 100023" required />
              </div>
              <label className="flex items-start gap-3 cursor-pointer select-none pt-1" data-testid="reg-terms-label">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#D4AF37]"
                  data-testid="reg-terms-checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  required
                />
                <span className="text-sm text-neutral-300">I accept the terms &amp; condition.</span>
              </label>
              <button className="lt-btn-primary w-full" data-testid="reg-submit-btn">
                Create account
              </button>
            </form>
            <div className="text-sm text-neutral-400 mt-6 text-center">
              Already a member? <Link to="/login" className="gold-text">Sign in</Link>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="lt-card p-8" data-testid="register-confirm-card">
            <div className="overline">Review &amp; Confirm</div>
            <h2 className="font-display text-3xl mt-2 mb-2">Confirm your details.</h2>
            <p className="text-sm text-neutral-400 mb-6">
              Please review everything carefully. After you confirm, a copy of these
              details will be downloaded to your device. Keep it safe — it includes
              your passwords.
            </p>

            {/* Capturable receipt */}
            <div
              ref={receiptRef}
              className="rounded-xl border border-[#D4AF37]/30 bg-[#0A0A0A] p-6"
              data-testid="register-receipt"
            >
              <div className="flex items-center gap-2 mb-4">
                <Diamond size={18} className="text-[#D4AF37]" />
                <span className="font-display text-xl">LT Global Club — Registration</span>
              </div>
              <div className="space-y-3">
                {FIELD_LABELS.map(([k, label]) => {
                  let value;
                  if (k === "mobile") {
                    value = form.mobile ? `${form.country_code} ${form.mobile}` : "—";
                  } else {
                    value = form[k] || "—";
                  }
                  return (
                    <div key={k} className="flex items-start justify-between gap-4 border-b border-white/10 pb-2">
                      <span className="text-xs uppercase tracking-widest text-neutral-500">{label}</span>
                      <span className="text-sm text-neutral-100 text-right break-all" data-testid={`receipt-${k}`}>
                        {value}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-neutral-600 mt-4">
                Generated {new Date().toLocaleString()} · globalclubtd.online
              </div>
            </div>

            {err && <div className="text-sm text-red-400 mt-4" data-testid="reg-error">{err}</div>}

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                type="button"
                onClick={() => setStep("form")}
                disabled={loading}
                className="lt-btn-secondary flex-1 inline-flex items-center justify-center gap-2"
                data-testid="reg-edit-btn"
              >
                <ArrowLeft size={16} /> Edit details
              </button>
              <button
                type="button"
                onClick={onConfirmSubmit}
                disabled={loading}
                className="lt-btn-primary flex-1 inline-flex items-center justify-center gap-2"
                data-testid="reg-confirm-submit-btn"
              >
                <ShieldCheck size={16} />
                {loading ? "Submitting…" : "Confirm & Submit"}
              </button>
            </div>

            <button
              type="button"
              onClick={downloadReceipt}
              className="w-full mt-3 text-sm gold-text inline-flex items-center justify-center gap-2"
              data-testid="reg-download-btn"
            >
              <Download size={14} /> Download details as image
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
